import {
  Close as CloseIcon,
  MarkEmailRead as MarkEmailReadIcon,
  NotificationsActive as NotificationsActiveIcon,
  Notifications as NotificationsIcon,
  VideoCall as VideoCallIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
} from "@mui/material";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../../common/hooks/useNotifications";
import { selectModule } from "../../../redux/slices/module.slice";
import { setOpenTaskUuid } from "../../../redux/slices/ehr/task.slice";
import { RootState } from "../../../redux/store/store";
import {
  actionButtonsStyle,
  clearButtonStyle,
  getChipProps,
  headerStyle,
  loadingStyle,
  markAllReadButtonStyle,
  noNotificationsStyle,
  notificationItemStyle,
  notificationListStyle,
  notificationTitleStyle,
  popoverPaperProps,
} from "./widget/styles";
import { NotificationPopoverProps } from "./widget/types";

const getNotificationIcon = (type?: string) => {
  switch (type) {
    case "TASK":
      return (
        <NotificationsActiveIcon sx={{ fontSize: 20, color: "#1976d2" }} />
      );
    case "REFERRAL":
      return <NotificationsIcon sx={{ fontSize: 20, color: "#ed6c02" }} />;
    case "VIDEO_CALL":
      return <VideoCallIcon sx={{ fontSize: 20, color: "#1976d2" }} />;
    default:
      return <NotificationsIcon sx={{ fontSize: 20, color: "#666" }} />;
  }
};

const cleanTaskBody = (text = "") => {
  return text.replace(/\(Task ID:.*?\)/, "").trim();
};

const getTaskNotificationContent = (notification: any) => {
  const commentContent = notification?.data?.commentContent;
  if (typeof commentContent === "string" && commentContent.trim()) {
    return commentContent;
  }

  return (
    cleanTaskBody(
      (notification?.data?.taskBody as string) ||
        (notification?.data?.title as string),
    ) || "Notification"
  );
};

const NotificationPopover: React.FC<NotificationPopoverProps> = ({
  anchorEl,
  onClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const { notifications, unreadCount, isLoading, isLoadingMore, error } =
    useSelector((state: RootState) => state.notification);
  const { markAsRead, markAllAsRead, isMarkingRead, loadMore, hasMore } =
    useNotifications();

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (
        scrollHeight - scrollTop - clientHeight < 80 &&
        hasMore &&
        !isLoadingMore
      ) {
        loadMore();
      }
    },
    [hasMore, isLoadingMore, loadMore],
  );

  const handleNotificationClick = (notification: any) => {
    if (!notification.isRead) {
      markAsRead(notification.uuid);
    }

    const notificationType = notification?.notificationType;
    let navigatePath = "";

    switch (notificationType) {
      case "TASK":
        const patientUuid = notification?.data?.patientUuid;
        const taskUuid = notification?.data?.taskUuid;
        const hasTaskCommentNotification =
          typeof notification?.data?.commentContent === "string" &&
          notification.data.commentContent.trim().length > 0;

        if (patientUuid) {
          const searchParams = new URLSearchParams();
          if (taskUuid) {
            searchParams.set("taskUuid", taskUuid);
          }
          if (hasTaskCommentNotification) {
            searchParams.set("openMessages", "true");
          }
          const queryString = searchParams.toString();
          navigatePath = `/ehr/patients/${patientUuid}/patienttasks/all${
            queryString ? `?${queryString}` : ""
          }`;
        } else {
          navigatePath = "/ehr/tasks/all";
        }

        if (taskUuid && !hasTaskCommentNotification) {
          dispatch(setOpenTaskUuid(taskUuid));
        }

        break;
      case "REFERRAL":
        navigatePath = "/ehr/referral";
        break;
      case "URGENT_MESSAGE":
        navigatePath = notification?.data?.patientUuid
          ? `/ehr/patients/${notification.data.patientUuid}/messages`
          : "/ehr/dashboard";
        break;
      case "VIDEO_CALL":
        navigatePath = notification?.data?.patientUuid
          ? `/ehr/patients/${notification.data.patientUuid}/messages`
          : "/ehr/dashboard";
        break;
      default:
        return;
    }

    if (navigatePath) {
      onClose();
      dispatch(selectModule("EHR"));
      navigate(navigatePath);
    }
  };

  const getNotificationDate = (notification: any): string | undefined => {
    return (
      notification?.created ||
      notification?.createdAt ||
      notification?.data?.created ||
      notification?.data?.createdAt ||
      notification?.data?.dueDate ||
      undefined
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "No date";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <Box>
        <Typography sx={{ fontSize: "11px", color: "#888", lineHeight: 1.2 }}>
          {formattedDate}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "#888", lineHeight: 1.2 }}>
          {formattedTime}
        </Typography>
      </Box>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: popoverPaperProps }}
    >
      <Box sx={headerStyle}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "16px" }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: "12px" }}
            />
          )}
        </Box>

        {notifications.length > 0 && (
          <Box sx={actionButtonsStyle}>
            <IconButton
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              size="small"
              sx={markAllReadButtonStyle}
            >
              <MarkEmailReadIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose} size="small" sx={clearButtonStyle}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={notificationListStyle} onScroll={handleScroll}>
        {isLoading ? (
          <Box sx={loadingStyle}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              Loading notifications...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={noNotificationsStyle}>
            <Typography variant="body2" sx={{ color: "#d32f2f" }}>
              Error loading notifications: {error}
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={noNotificationsStyle}>
            <NotificationsIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
            <Typography variant="body2" sx={{ color: "#666" }}>
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {notifications.map((notification, index) => {
              const chipProps = getChipProps(notification.notificationType);
              const dateString = getNotificationDate(notification);

              return (
                <React.Fragment key={notification?.uuid || index}>
                  <ListItem
                    sx={{
                      ...notificationItemStyle,
                      backgroundColor:
                        notification?.isRead === false
                          ? "#f8f9fa"
                          : "transparent",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          notification?.isRead === false
                            ? "#e8f4fd"
                            : "#f5f5f5",
                      },
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          backgroundColor:
                            notification?.isRead === false
                              ? "#e3f2fd"
                              : "#f5f5f5",
                        }}
                      >
                        {getNotificationIcon(notification.notificationType)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            {notification?.notificationType === "VIDEO_CALL" ? (
                              <>
                                <Typography
                                  sx={{
                                    ...notificationTitleStyle,
                                    fontWeight: 600,
                                    mb: 0.5,
                                  }}
                                >
                                  {notification?.data?.senderName as string}
                                </Typography>
                                <Typography sx={{ ...notificationTitleStyle }}>
                                  started video calling
                                </Typography>
                              </>
                            ) : notification?.notificationType ===
                              "URGENT_MESSAGE" ? (
                              <>
                                {notification?.data?.senderName && (
                                  <Typography
                                    sx={{
                                      ...notificationTitleStyle,
                                      fontWeight: 600,
                                      mb: 0.5,
                                    }}
                                  >
                                    {String(notification.data.senderName)}
                                  </Typography>
                                )}
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    color: "#666",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {String(
                                    notification?.data?.content ||
                                      "Notification",
                                  )}
                                </Typography>
                              </>
                            ) : (
                              <Typography sx={notificationTitleStyle}>
                                {getTaskNotificationContent(notification)}
                              </Typography>
                            )}
                          </Box>

                          <Chip
                            label={chipProps.label}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "10px",
                              fontWeight: 600,
                              borderRadius: "6px",
                              ...chipProps.sx,
                            }}
                          />
                        </Box>
                      }
                      secondary={formatDate(dateString)}
                    />
                  </ListItem>

                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}

            {isLoadingMore && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 1.5,
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </List>
        )}
      </Box>
    </Popover>
  );
};

export default NotificationPopover;
